import type { LocationData } from '../locationData';

interface Props {
  location: LocationData;
}

const primaryServices = [
  {
    icon: 'ri-scissors-cut-line',
    title: 'Lawn Mowing',
    desc: 'Weekly mowing, edging, and trimming to keep your lawn neat and healthy all season long.',
    color: 'green',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Gardening',
    desc: 'Planting, mulching, weeding, pruning, and fertilizing to keep your gardens thriving.',
    color: 'green',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Yard Cleanups',
    desc: 'Spring and fall cleanups including leaf removal, debris clearing, and bed preparation.',
    color: 'green',
  },
  {
    icon: 'ri-snowy-line',
    title: 'Snow Removal',
    desc: 'Full-service snow plowing, snow blowing, walkway clearing, and de-icing all winter.',
    color: 'cyan',
  },
];

const secondaryServices = [
  {
    icon: 'ri-road-map-line',
    title: 'Driveway Hardscaping',
    desc: 'Custom interlock driveway installations to boost curb appeal and add lasting value to your home.',
    color: 'yellow',
  },
  {
    icon: 'ri-building-line',
    title: 'Patio Hardscaping',
    desc: 'Beautiful stone and interlock patios designed and built to expand your outdoor living space.',
    color: 'yellow',
  },
];

export default function LocationServices({ location }: Props) {
  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-block px-4 py-2 bg-yellow-50 text-yellow-700 rounded-full text-sm font-semibold mb-4">
            Our Services in {location.name}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Complete Property Care for {location.name} Homeowners
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From spring cleanups to winter snow removal — everything your property needs, all year round.
          </p>
        </div>

        {/* Primary Services */}
        <div className="mb-6">
          <p className="text-xs font-bold text-gray-400 tracking-widest uppercase text-center mb-6">Primary Services</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {primaryServices.map((s, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-6 border-t-4 hover:-translate-y-1 transition-all duration-300 ${
                  s.color === 'green' ? 'border-green-500' : 'border-cyan-500'
                }`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${
                  s.color === 'green' ? 'bg-green-50' : 'bg-cyan-50'
                }`}>
                  <i className={`${s.icon} text-2xl ${
                    s.color === 'green' ? 'text-green-600' : 'text-cyan-600'
                  }`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Secondary Services */}
        <div className="mt-10">
          <p className="text-xs font-bold text-gray-400 tracking-widest uppercase text-center mb-6">Hardscaping &amp; Construction</p>
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {secondaryServices.map((s, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 border-t-4 border-yellow-400 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-50 rounded-xl mb-4">
                  <i className={`${s.icon} text-2xl text-yellow-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Neighbourhoods */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
            Neighbourhoods We Serve in {location.name}
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {location.neighborhoods.map((n, i) => (
              <span
                key={i}
                className="inline-flex items-center space-x-1 px-4 py-2 bg-white rounded-full border border-gray-200 text-gray-700 text-sm font-medium"
              >
                <i className="ri-map-pin-line text-green-500"></i>
                <span>{n}</span>
              </span>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">
            Don&apos;t see your neighbourhood? <a href="tel:+16479837924" className="text-green-600 font-semibold hover:underline cursor-pointer">Call us</a> — we likely cover it!
          </p>
        </div>
      </div>
    </section>
  );
}
